/**
 * LocationSettings Component
 *
 * Manages farm locations/rooms configuration.
 * Allows adding, editing, deleting, and reordering locations.
 */

import { useState, useEffect } from 'react'
import { PageLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction
} from '@/components/ui/alert-dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  Plus,
  Pencil,
  Trash2,
  MapPin,
  Building2,
  Box,
  Wrench,
  GripVertical,
  Eye,
  EyeOff
} from 'lucide-react'
import {
  fetchLocations,
  createLocation,
  updateLocation,
  deleteLocation,
  toggleLocationActive
} from '@/supabase-client'

// Location type options with icons
const LOCATION_TYPES = [
  { value: 'room', label: 'Room', icon: Building2 },
  { value: 'area', label: 'Area', icon: MapPin },
  { value: 'zone', label: 'Zone', icon: Box },
  { value: 'equipment', label: 'Equipment', icon: Wrench }
]

// Expected status options for containers
const STATUS_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'incubating', label: 'Incubating' },
  { value: 'fruiting', label: 'Fruiting' },
  { value: 'pinning', label: 'Pinning' },
  { value: 'resting', label: 'Resting' },
  { value: 'harvesting', label: 'Harvesting' },
  { value: 'cleaning', label: 'Cleaning' }
]

const getTypeIcon = (type) => {
  const typeConfig = LOCATION_TYPES.find(t => t.value === type)
  return typeConfig?.icon || MapPin
}

const getTypeBadgeColor = (type) => {
  switch (type) {
    case 'room': return 'bg-blue-100 text-blue-800'
    case 'area': return 'bg-green-100 text-green-800'
    case 'zone': return 'bg-purple-100 text-purple-800'
    case 'equipment': return 'bg-orange-100 text-orange-800'
    default: return 'bg-gray-100 text-gray-800'
  }
}

const emptyFormData = {
  code: '',
  name: '',
  location_type: 'room',
  expected_status: 'none',
  description: '',
  capacity: ''
}

export default function LocationSettings({ user, onLogout }) {
  const [locations, setLocations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [showInactive, setShowInactive] = useState(false)

  // Dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [editingLocation, setEditingLocation] = useState(null)
  const [deletingLocation, setDeletingLocation] = useState(null)
  const [formData, setFormData] = useState(emptyFormData)
  const [saving, setSaving] = useState(false)

  // Load locations on mount and when showInactive changes
  useEffect(() => {
    loadLocations()
  }, [showInactive])

  const loadLocations = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchLocations({ includeInactive: showInactive })
      setLocations(data)
    } catch (err) {
      setError('Failed to load locations: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenDialog = (location = null) => {
    if (location) {
      setEditingLocation(location)
      setFormData({
        code: location.code,
        name: location.name,
        location_type: location.location_type,
        expected_status: location.expected_status || 'none',
        description: location.description || '',
        capacity: location.capacity?.toString() || ''
      })
    } else {
      setEditingLocation(null)
      setFormData(emptyFormData)
    }
    setIsDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setEditingLocation(null)
    setFormData(emptyFormData)
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      const locationData = {
        ...formData,
        capacity: formData.capacity ? parseInt(formData.capacity, 10) : null,
        expected_status: formData.expected_status === 'none' ? null : formData.expected_status
      }

      if (editingLocation) {
        await updateLocation(editingLocation.id, locationData)
        setSuccess('Location updated successfully')
      } else {
        // For new locations, set sort_order to be after the last one
        const maxSortOrder = locations.reduce((max, loc) => Math.max(max, loc.sort_order || 0), 0)
        locationData.sort_order = maxSortOrder + 10
        await createLocation(locationData)
        setSuccess('Location created successfully')
      }

      handleCloseDialog()
      loadLocations()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      if (err.message?.includes('duplicate key') || err.code === '23505') {
        setError('A location with this code already exists')
      } else {
        setError('Failed to save location: ' + err.message)
      }
    } finally {
      setSaving(false)
    }
  }

  const handleOpenDeleteDialog = (location) => {
    setDeletingLocation(location)
    setIsDeleteDialogOpen(true)
  }

  const handleDelete = async () => {
    if (!deletingLocation) return

    try {
      await deleteLocation(deletingLocation.id)
      setSuccess('Location deleted successfully')
      setIsDeleteDialogOpen(false)
      setDeletingLocation(null)
      loadLocations()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError('Failed to delete location: ' + err.message)
    }
  }

  const handleToggleActive = async (location) => {
    try {
      await toggleLocationActive(location.id, !location.is_active)
      setSuccess(`Location ${location.is_active ? 'deactivated' : 'activated'} successfully`)
      loadLocations()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError('Failed to update location: ' + err.message)
    }
  }

  if (loading && locations.length === 0) {
    return (
      <PageLayout
        title="Location Settings"
        user={user}
        onLogout={onLogout}
      >
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
          <p className="text-muted-foreground">Loading locations...</p>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title="Location Settings"
      user={user}
      onLogout={onLogout}
    >
      <div className="h-full overflow-y-auto">
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription className="flex justify-between items-center">
            {error}
            <Button variant="ghost" size="sm" onClick={() => setError(null)}>×</Button>
          </AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-6">
          <AlertDescription className="flex justify-between items-center">
            {success}
            <Button variant="ghost" size="sm" onClick={() => setSuccess(null)}>×</Button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Farm Locations</CardTitle>
              <CardDescription>
                Configure rooms, areas, and zones used for inspections and container tracking
              </CardDescription>
            </div>
            <Button onClick={() => handleOpenDialog()} className="gap-2">
              <Plus className="h-4 w-4" />
              Add Location
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Show inactive toggle */}
          <div className="flex items-center gap-2 mb-4">
            <Switch
              id="show-inactive"
              checked={showInactive}
              onCheckedChange={setShowInactive}
            />
            <Label htmlFor="show-inactive" className="text-sm text-muted-foreground cursor-pointer">
              Show inactive locations
            </Label>
          </div>

          {locations.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No locations configured yet</p>
              <p className="text-sm mt-1">Add your first location to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Expected Status</TableHead>
                  <TableHead>Capacity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {locations.map((location) => {
                  const TypeIcon = getTypeIcon(location.location_type)
                  return (
                    <TableRow
                      key={location.id}
                      className={!location.is_active ? 'opacity-50' : ''}
                    >
                      <TableCell>
                        <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{location.code}</TableCell>
                      <TableCell className="font-medium">{location.name}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={getTypeBadgeColor(location.location_type)}>
                          <TypeIcon className="h-3 w-3 mr-1" />
                          {location.location_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {location.expected_status ? (
                          <Badge variant="outline">{location.expected_status}</Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {location.capacity || <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(location)}
                          className="gap-1"
                        >
                          {location.is_active ? (
                            <>
                              <Eye className="h-4 w-4 text-green-600" />
                              <span className="text-green-600">Active</span>
                            </>
                          ) : (
                            <>
                              <EyeOff className="h-4 w-4 text-muted-foreground" />
                              <span className="text-muted-foreground">Inactive</span>
                            </>
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(location)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDeleteDialog(location)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingLocation ? 'Edit Location' : 'Add New Location'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Code *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => handleInputChange('code', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                    placeholder="e.g., fruiting_3"
                    required
                    disabled={!!editingLocation}
                  />
                  <p className="text-xs text-muted-foreground">
                    Unique identifier (lowercase, underscores)
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Display Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="e.g., Fruiting Room 3"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="location_type">Type *</Label>
                  <Select
                    value={formData.location_type}
                    onValueChange={(value) => handleInputChange('location_type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LOCATION_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="h-4 w-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expected_status">Expected Status</Label>
                  <Select
                    value={formData.expected_status}
                    onValueChange={(value) => handleInputChange('expected_status', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status..." />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(status => (
                        <SelectItem key={status.value || 'none'} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Expected container status in this location
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="capacity">Capacity</Label>
                <Input
                  id="capacity"
                  type="number"
                  min="0"
                  value={formData.capacity}
                  onChange={(e) => handleInputChange('capacity', e.target.value)}
                  placeholder="Maximum containers (optional)"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Optional notes about this location..."
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : editingLocation ? 'Save Changes' : 'Add Location'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Location</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingLocation?.name}"? This action cannot be undone.
              Consider deactivating the location instead if you might need it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingLocation(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              variant="destructive"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  )
}
